import React from "react";
import TextWithTooltip from "./TextWithTooltip";
import ElementWithGenericTrigger from "../Utils/ElementWithGenericTrigger";

/**
 * Card de contexto para exibir pares de label e valor.
 *
 * @param {Object} props
 * @param {Array<Object>} props.data - Lista de itens do contexto
 * @param {string} props.data[].label - Texto do título
 * @param {string} props.data[].value - Valor exibido
 * @param {boolean} [props.isRenderModal=false] - Define padding reduzido
 * @param {boolean} [props.enableTooltip=true] - Habilita o tooltip no valor exibido
 *
 * @example
 * <CardContext
 *   data={[
 *     { label: "Nº Pedido", value: "123456" },
 *     { label: "Saldo", value: "R$ 500,00" }
 *   ]}
 * />
 */
const CardContext = ({
  data = [],
  isRenderModal = false,
  enableTooltip = true,
}) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  const getComponent = (item, idx, isUserPhoto = false) => {
    switch (item?.tipo) {
      case "image":
        const imageUrl = item?.valor;

        const imageClass = isUserPhoto
          ? "img-context-user d-flex align-items-center bg-transparent"
          : "img-card font-size-32 d-flex align-items-center bg-transparent";
        // const fallbackIconClass = isUserPhoto
        //   ? 'far fa-user-circle img-context-user d-flex align-items-center bg-transparent'
        //   : 'far fa-user-circle img-card font-size-32 d-flex align-items-center bg-transparent'

        return (
          <ElementWithGenericTrigger
            key={idx}
            elementID={`image-${idx}`}
            elementValue={imageUrl}
            elementType="image-tooltip"
            elementDescription={item?.descricao}
            elementClassName={`${imageClass}`}
            fnInString={item?.acao}
          />
        );
      case "icone":
        return (
          <ElementWithGenericTrigger
            key={idx}
            elementID={`icone-${idx}`}
            elementValue={`${item?.valor}`}
            elementType="icon-tooltip"
            elementDescription={item?.descricao}
            fnInString={item?.acao}
          />
        );
      case "badge":
        return (
          <ElementWithGenericTrigger
            key={idx}
            elementID={`badge-${idx}`}
            elementValue={imageUrl}
            elementType="badge"
            elementDescription={item?.descricao}
            elementClassName={`${item?.estilo}`}
            fnInString={item?.acao}
          />
        );
      case "text":
      case "texto":
        return (
          <ElementWithGenericTrigger
            key={idx}
            elementID={`text-${idx}`}
            elementValue={`${item?.valor}`}
            elementType="text"
            elementClassName={`text-color-azul1 font-size-16 font-w-600 card-context-text`}
            elementDescription={item?.descricao}
            fnInString={item?.acao}
          />
        );
      default:
        return (
          <i
            key={idx}
            className="far fa-user-circle"
            aria-hidden="true"
            title="Tipo de componente desconhecido ou valor ausente"
          />
        );
    }
  };

  return (
    <div className="card-context-dynamic-wrapper d-flex flex-column" style={{ flex: "auto" }}>
      <div
        className={`card-context-dynamic d-flex justify-content-between align-items-start ${
          isRenderModal ? "p-3" : "p-4"
        } flex-wrap`}
      >
        {data.map((item, index) => {
          const titulo = item?.label ?? "";
          const valor = item?.value ?? "";
          const largura = item?.largura ?? "col";
          let hasObject = false;

          try {
            if (
              typeof item?.value === "string" &&
              (item.value.startsWith("{") || item.value.startsWith("["))
            ) {
              hasObject = JSON.parse(item.value);
            }
          } catch (error) {}

          if (!titulo && !valor) return null;

          if (hasObject) {
            return (
              <div
                key={`${titulo}-${index}`}
                className={`card-context-dynamic-item d-flex flex-column ${largura}`}
              >
                <span className="font-size-14 font-w-500 mb-1 text-color-cinza5">
                  {titulo}
                </span>
                <div className="d-flex align-items-center gap-1">
                  {Array.isArray(hasObject) && hasObject.length > 0
                    ? hasObject.map((innerItem, innerIndex) =>
                        getComponent(innerItem, `${index}-${innerIndex}`),
                      )
                    : getComponent(item, index)}
                </div>
              </div>
            );
          }

          return (
            <div
              key={`${titulo}-${index}`}
              className={`card-context-dynamic-item d-flex flex-column ${largura}`}
            >
              <span className="font-size-14 font-w-500 mb-1 text-color-cinza5">
                {titulo}
              </span>

              {valor && (
                <span className="text-color-azul1 font-size-16 font-w-600 card-context-text">
                  {enableTooltip ? (
                    <TextWithTooltip id={index} textFull={valor} text={valor} />
                  ) : (
                    valor
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CardContext;
